import { HighlightContentPreviewId } from '@/constants/enum';
import useClickHighlight from '@/hooks/useClickHighlight';
import { highlightPreviewSelector } from '@/redux/slice/highlightPreview.slice';
import { dataSettingsSelector, locationInfoSelector } from '@/redux/slice/settings.slice';
import { IRule } from '@/types/newSetup.type';
import parse from 'html-react-parser';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PreviewMethod from './PreviewMethod';
import { stringify } from '@/helpers/string';
import previewSlice from '@/redux/slice/preview.slice';

interface IProps {
  rules: IRule[];
  zipcodePlaceholder?: string;
  isAutoDetect?: boolean;
}

interface IState {
  rules?: IRule[];
  inputValue: string;
  inputSubmitValue: string;
  showContent: boolean;
  selectedRule?: IRule;
}

const PreviewZipcode = (props: IProps) => {
  const dispatch = useDispatch();
  const dataSettings = useSelector(dataSettingsSelector);
  const locationInfo = useSelector(locationInfoSelector);
  const { zipcodeSetting } = dataSettings;
  const { handleClickHighlight } = useClickHighlight();
  const {
    isHighlightedZipcodeCheckerLabel,
    isHighlightedZipcodeCheckerInputIndicator,
    isHighlightedZipcodeCheckerSubmitButton,
    isHighlightedZipcodeCheckerValidText,
    isHighlightedZipcodeCheckerInvalidText,
  } = useSelector(highlightPreviewSelector);

  const [state, setState] = useState<IState>({
    inputValue: '',
    inputSubmitValue: '',
    showContent: false,
    selectedRule: undefined,
  });

  const handleChange = (key: keyof typeof state) => (value: string) => {
    setState({
      ...state,
      [key]: value,
    });
  };

  const handleCheckZipcode = useCallback(
    (inputValue?: string) => {
      const value = inputValue ?? state.inputValue;

      if (!value) {
        handleClickHighlight('isHighlightedZipcodeCheckerSubmitButtonSetting')();
        return;
      }
      const validRules = props.rules.filter((rule) => {
        const temp = rule?.locationConditions
          ?.find((condition) => condition?.type === 'zipcode')
          ?.value[0]?.data?.split(',')
          ?.some((zipcode) => {
            if (zipcode?.includes(':')) {
              let tempSpecificZipcode = value;
              let [start, end] = zipcode?.split(':');

              // Check if in case Brazil zipcode
              if (tempSpecificZipcode?.includes('-') && start?.includes('-') && end?.includes('-')) {
                tempSpecificZipcode = value.replace(/-/g, '.');
                start = start.replace(/-/g, '.');
                end = end.replace(/-/g, '.');
              }
              return !isNaN(+start) && !isNaN(+end) && +start <= +tempSpecificZipcode && +end >= +tempSpecificZipcode;
            } else {
              return value === zipcode;
            }
          });
        return temp;
      });

      const baseRules = props.rules.filter(
        (rule) =>
          !rule?.locationConditions?.find((condition) => condition?.type === 'zipcode') &&
          !rule?.locationConditions?.find((condition) => condition?.type === 'country'),
      );

      const newRules = [...validRules, ...baseRules];

      setState((prev) => ({
        ...prev,
        inputSubmitValue: value,
        rules: newRules.length > 0 ? newRules : undefined,
        showContent: !!value,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleClickHighlight, stringify(props.rules), state.inputValue],
  );

  useEffect(() => {
    if (props.isAutoDetect && locationInfo?.data?.zip_code) {
      handleCheckZipcode(locationInfo?.data?.zip_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationInfo?.data?.zip_code, props.isAutoDetect]);

  useEffect(() => {
    dispatch(previewSlice.actions.handleSelectedZipcode(state.inputSubmitValue));
    return () => {
      dispatch(previewSlice.actions.handleSelectedZipcode(''));
    };
  }, [dispatch, state.inputSubmitValue]);

  return (
    <div className="ot-estimated-shipping-zipcode-layout">
      {/* Postcode has not been submitted */}
      {!state.rules ? (
        <label htmlFor={HighlightContentPreviewId.ZipcodeCheckerLabel}>
          <div
            className={`ot-estimated-shipping-zipcode-submit-btn-title mb-8 ${
              isHighlightedZipcodeCheckerLabel ? 'item-highlight-outline' : ''
            }`}
          >
            {parse(zipcodeSetting.inputLabel || '')}
          </div>
        </label>
      ) : null}
      {state.rules ? null : (
        <div className="ot-estimated-shipping-zipcode-submit-btn-input-form mt-8">
          <input
            className={`ot-estimated-shipping-zipcode-input ${
              isHighlightedZipcodeCheckerInputIndicator ? 'item-highlight-outline' : ''
            }`}
            placeholder={props.zipcodePlaceholder || zipcodeSetting.inputPlaceholder}
            value={state.inputValue}
            onChange={(event: any) => handleChange('inputValue')(event.target.value)}
            onClick={handleClickHighlight('isHighlightedZipcodeCheckerInputIndicatorSetting')}
          />
          <p
            className={`ot-estimated-shipping-zipcode-submit-btn ${
              isHighlightedZipcodeCheckerSubmitButton ? 'item-highlight-outline' : ''
            }`}
            style={{
              backgroundColor: zipcodeSetting.submitButtonBackgroundColor,
              color: zipcodeSetting.submitButtonTextColor,
              marginLeft: '5px',
            }}
            onClick={() => handleCheckZipcode()}
          >
            {zipcodeSetting.submitButton}
          </p>
        </div>
      )}

      {/* Postcode has been submitted */}
      {state.showContent ? (
        <>
          <p>
            {state.rules ? (
              <p className="ot-estimated-shipping-zipcode-available">
                <span className="est-confirm-icon">✔ </span>
                <label
                  className={`ot-estimated-shipping-zipcode-available-text ${
                    isHighlightedZipcodeCheckerValidText ? 'item-highlight-outline' : ''
                  }`}
                  htmlFor={HighlightContentPreviewId.ZipcodeValidText}
                >
                  {parse(zipcodeSetting.zipcodeAvailableText || '')}{' '}
                  <span className="ot-estimated-shipping-zipcode-available-value">{state.inputSubmitValue}</span>
                </label>
                <span
                  className="ot-estimated-shipping-zipcode-show-input-zipcode-form"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setState({ inputValue: '', inputSubmitValue: '', rules: undefined, showContent: false });
                  }}
                >
                  {'  '}
                  <svg
                    fill={dataSettings.text_color}
                    style={{ marginBottom: '-3px' }}
                    height="16.48"
                    width="11.36102"
                    viewBox="0 0 768.051 1024"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M655.461 473.469c11.875 81.719-13.062 167.781-76.812 230.594-94.188 92.938-239.5 104.375-346.375 34.562l74.875-73L31.96 627.25 70.367 896l84.031-80.5c150.907 111.25 364.938 100.75 502.063-34.562 79.5-78.438 115.75-182.562 111.25-285.312L655.461 473.469zM189.46 320.062c94.156-92.938 239.438-104.438 346.313-34.562l-75 72.969 275.188 38.406L697.586 128l-83.938 80.688C462.711 97.34400000000005 248.742 107.96900000000005 111.585 243.25 32.085 321.656-4.133 425.781 0.335 528.5l112.25 22.125C100.71 468.875 125.71 382.906 189.46 320.062z" />
                  </svg>
                </span>
              </p>
            ) : (
              <label
                className={`${isHighlightedZipcodeCheckerInvalidText ? 'item-highlight-outline' : ''}`}
                htmlFor={HighlightContentPreviewId.ZipcodeInvalidText}
              >
                <p className="ot-estimated-shipping-zipcode-notice-text">
                  {parse(zipcodeSetting.zipcodeNotAvailableText || '')}{' '}
                  <span className="ot-estimated-shipping-zipcode-not-available">{state.inputSubmitValue}</span>
                </p>
              </label>
            )}
          </p>
          <PreviewMethod rules={state.rules} isEmptyRule={props.rules.length === 0} zipcode={state.inputSubmitValue} />
        </>
      ) : null}
    </div>
  );
};

export default PreviewZipcode;
